import React, { useState, useEffect, useRef } from "react";
import "../styles/signup.css";
import { 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  PersonAdd,
  CheckCircle,
  Security
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import ReCAPTCHA from 'react-google-recaptcha';
import { API_URL } from '../config';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcNFwktAAAAAKbvR33vzPo9xFQNMS5ocxfImKMZ';

const Signup = () => {
  const SECRET_QUESTIONS = [
    "Quel est le nom de votre premier personnage ?",
    "Quelle est votre classe préférée dans Nexus ?",
    "Dans quelle ville avez-vous commencé votre aventure ?",
    "Quel était le nom de votre première guilde ?",
    "Quel est le nom de votre animal de compagnie ?",
    "Quel est votre donjon favori dans Nexus ?",
  ];

  const [formData, setFormData] = useState({
    username: "",
    pseudo: "",
    password: "",
    email: "",
    secret_question: "",
    secret_answer: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => setFingerprint(result.visitorId))
      .catch(() => {}); // silencieux si le navigateur bloque
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!captchaToken) {
      setMessage({ type: 'error', text: 'Veuillez valider le CAPTCHA avant de continuer.' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_URL + "/account/createAccount", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, fingerprint, captchaToken }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: "success", text: data.message });
        setFormData({
          username: "",
          pseudo: "",
          password: "",
          email: "",
          secret_question: "",
          secret_answer: "",
        });
        setCaptchaToken(null);
        if (recaptchaRef.current) recaptchaRef.current.reset();
      } else {
        const errorData = await response.json();
        let errorText = errorData.error;
        if (errorData.debug_accounts) {
          errorText += ` (comptes existants : ${errorData.debug_accounts})`;
        }
        setMessage({ type: "error", text: errorText });
      }
    } catch (error) {
      console.error("Erreur lors de la création du compte :", error);
      setMessage({ type: "error", text: "Erreur lors de la création du compte." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="signup-page">
      <Container maxWidth="sm" sx={{ paddingTop: '120px', paddingBottom: '60px' }}>
        <Paper 
          elevation={8}
          className="signup-card"
          sx={{
            padding: 4,
            background: 'linear-gradient(145deg, rgba(26, 10, 46, 0.9) 0%, rgba(20, 25, 45, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(182, 122, 60, 0.2)',
            borderRadius: '20px'
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
            <PersonAdd 
              sx={{ 
                fontSize: 60, 
                color: '#B67A3C',
                marginBottom: 2
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 1
              }}
            >
              Créer un compte
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              Rejoignez l'aventure <strong>Nexus</strong>
            </Typography>
          </Box>

          {message && (
            <Alert 
              severity={message.type} 
              sx={{ 
                marginBottom: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  color: message.type === 'success' ? '#B67A3C' : undefined
                }
              }}
              icon={message.type === 'success' ? <CheckCircle /> : undefined}
            >
              {message.text}
            </Alert>
          )}

          <form className="signup-form" onSubmit={handleSubmit}>
            <TextField 
              label="Nom d'utilisateur"
              name="username"
              variant="outlined"
              fullWidth
              required
              value={formData.username}
              onChange={handleChange}
              sx={{ marginBottom: 3 }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <TextField 
              label="Pseudo (affiché dans le classement)"
              name="pseudo"
              variant="outlined"
              fullWidth
              required
              helperText="3-20 caractères — visible publiquement, non modifiable"
              value={formData.pseudo}
              onChange={handleChange}
              sx={{ marginBottom: 3 }}
              FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <TextField 
              label="Mot de passe"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              value={formData.password}
              onChange={handleChange}
              sx={{ marginBottom: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <TextField 
              label="Adresse email"
              name="email"
              type="email"
              variant="outlined"
              fullWidth
              required
              helperText="⚠️ Cet email sert à confirmer votre connexion — assurez-vous d'y avoir accès"
              value={formData.email}
              onChange={handleChange}
              sx={{ marginBottom: 3 }}
              FormHelperTextProps={{ sx: { color: 'rgba(255, 200, 0, 0.8)', fontWeight: 500 } }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(182, 122, 60, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B67A3C',
                  }
                }
              }}
            />

            <Box sx={{ marginBottom: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security sx={{ color: 'rgba(182, 122, 60, 0.7)', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
                Question secrète — utilisée pour modifier votre mot de passe ou email
              </Typography>
            </Box>

            <FormControl fullWidth required sx={{ marginBottom: 3 }}>
              <InputLabel sx={{ color: 'rgba(182, 122, 60, 0.7)' }}>Question secrète</InputLabel>
              <Select
                name="secret_question"
                value={formData.secret_question}
                onChange={handleChange}
                label="Question secrète"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#B67A3C' },
                }}
              >
                {SECRET_QUESTIONS.map((q) => (
                  <MenuItem key={q} value={q}>{q}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField 
              label="Réponse secrète"
              name="secret_answer"
              variant="outlined"
              fullWidth
              required
              value={formData.secret_answer}
              onChange={handleChange}
              sx={{ marginBottom: 4 }}
              InputProps={{
                sx: {
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(182, 122, 60, 0.5)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#B67A3C' },
                }
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
                onErrored={() => setCaptchaToken(null)}
                theme="dark"
              />
            </Box>

            <Button 
              variant="contained" 
              type="submit" 
              fullWidth 
              size="large"
              disabled={loading || !captchaToken}
              sx={{
                background: 'linear-gradient(135deg, #B67A3C 0%, #A7AEB8 100%)',
                color: '#ffffff',
                fontWeight: 700,
                padding: '14px',
                fontSize: '1.1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #8D5A2A 0%, #8D5A2A 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(182, 122, 60, 0.4)'
                },
                '&:disabled': {
                  background: 'rgba(182, 122, 60, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Créer mon compte"}
            </Button>

            <Box sx={{ textAlign: 'center', marginTop: 3 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Déjà inscrit ?{' '}
                <MuiLink 
                  component={Link} 
                  to="/login" 
                  sx={{ 
                    color: '#B67A3C',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Se connecter
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Signup;